/* eslint-disable react/prop-types */
import React, { useRef } from "react";
import Select, { components, createFilter } from "react-select";
import Creatable from "react-select/creatable";
import { classes } from "components/Utility";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { fontColor } from "components/Shared/systemColors";
import Tooltip from "@mui/material/Tooltip";
import Zoom from "@mui/material/Zoom";

export const ReactSelectClassName = "react-select-container";

export interface MSelectOption<T = string | number> {
  value: T;
  label: string;
}

export interface MSelectProps {
  /** id for the subcomponenet */
  id?: string;
  /** Class name to pass to subcomponent  */
  className?: string;
  /** Selected value  */
  value?: MSelectOption | MSelectOption[];
  /** Default value  */
  defaultValue?: MSelectOption | MSelectOption[];
  /** List of options for dropdown  */
  options: MSelectOption[];
  /** Label for all option. Default is "Select All"  */
  allOptionLabel?: string;
  /** Display an option to select all items in dropdown. Default is true */
  enableAllOption?: boolean;
  /** Func to call after dropdown selection*/
  onChange: {
    (newValue?: MSelectOption | MSelectOption[]): void;
    (newValue?: MSelectOption): void;
  };
  /** Func to call when dropdown is focused */
  onFocus?: () => void;
  /** Func to call when dropdown is blurred */
  onBlur?: () => void;
  /** Allow user to clear selection. Default is true */
  isClearable?: boolean;
  /** Allow user to select multiple options. Default is false */
  isMulti?: boolean;
  /** If true and isMulti, selected values will not be shown in the drown after blur. Default is false  */
  hideMultiSelected?: boolean;
  /** If true, hide dropdown arrow. Default is false */
  hideArrow?: boolean;
  /** Optional function for sorting dropdown options */
  sortFunc?: (a: any, b: any) => number;
  /** Allow user to create new options. Default is false */
  creatable?: boolean;
  /** Optional function to determine if a new option is valid (only used when creatable is true) */
  isValidNewOption?: (inputValue: string) => boolean;
  /** Optional function that returns the target element for portal. Default is document.querySelector("body") */
  getMenuPortalTarget?: () => Element;
  /** No padding in styles. Default is false */
  noPadding?: boolean;
  /** Use default select control styles for dropdown options. Default is false */
  noOptionComponentStyles?: boolean;
  /** Text to display before selection is made */
  placeholder?: string;
  /** Control label */
  label?: string;
  /** Passed to subcomponent. TODO: Figure out if this is needed */
  itemLabel?: string;
  /** Passed to subcomponent. TODO: Figure out if this is needed */
  itemValue?: string;
  /** Passed to subcomponent. TODO: Figure out if this is needed */
  name?: string;
  /** Controls where the menu opens relative to the control. Default is "bottom" */
  menuPlacement?: "auto" | "bottom" | "top";
}

const MSelect = (props: MSelectProps) => {
  const selectControlRef = useRef({});
  const SELECT_ALL_VALUE = "SELECT_ALL_VALUE";

  // Styles and components

  const styles = {
    control: (provided) => ({
      ...provided,
      borderColor: null,
      boxShadow: null,
      backgroundColor: "initial",
      minHeight: "0px",
      border: "none",
    }),
    dropdownIndicator: () => ({
      color: fontColor.primary,
    }),
    clearIndicator: () => ({
      color: fontColor.primary,
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    menu: (provided) => ({
      ...provided,
      width: "fit-content",
      minWidth: "100%",
    }),
    menuList: (provided) => ({
      ...provided,
      width: "fit-content",
      minWidth: "100%",
    }),
    option: (provided, state) => ({
      ...provided,
      whiteSpace: "nowrap",
      backgroundColor: null,
      color: state.isSelected ? "var(--primary-color)" : "var(--primary-font-color)",
      width: "fit-content",
      minWidth: "100%",
      ":active": {
        backgroundColor: "rgba(0, 0, 0, .24)",
      },
      ":hover": {
        backgroundColor: "rgba(0, 0, 0, .12)",
      },
      ".checkbox": {
        pointerEvents: "none",
        width: "fit-content !important",
        //Needed as React-md media queries overwrite this when dropdown portal placed in small container
        padding: "0px",
        height: "25px",
      },
      label: {
        color: "inherit",
        whiteSpace: "nowrap",
      },
      i: {
        fontSize: "25px !important",
      },
      //Needed as React-md media queries overwrite this when dropdown portal placed in small container
      ".md-btn--icon": {
        height: "25px !important",
        width: "40px !important",
        padding: "0px !important",
      },
    }),
    valueContainer: ({ padding, ...provided }) => ({
      ...provided,
      fontSize: 13,
      padding: props.noPadding ? 0 : padding,
      color: "var(--primary-font-color)",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "var(--primary-font-color)",
    }),
    multiValue: (provided) => ({
      ...provided,
      color: "var(--primary-font-color)",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "var(--primary-font-color)",
    }),
    input: (provided) => ({
      ...provided,
      color: "var(--primary-font-color)",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "var(--secondary-font-color)",
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 20,
    }),
  };

  const getSelectComponent = () => {
    return props.creatable ? Creatable : Select;
  };

  const getSubComponents = () => {
    const subComponents = {};
    if (props.isMulti) {
      // eslint-disable-next-line react/display-name
      subComponents["Option"] = ({ children, ...props }) => (
        // @ts-ignore - react-select typing issue
        <components.Option className={"md-text-field"} {...props}>
          <FormControlLabel
            className={`checkbox`}
            control={<Checkbox checked={props.isSelected} onChange={() => {}} />}
            label={children}
          />
        </components.Option>
      );
      if (!props.hideMultiSelected) {
        // eslint-disable-next-line react/display-name
        subComponents["MultiValue"] = ({ getValue, children }) => {
          const value = getValue();
          if (value && value.length && children === value[0].label) {
            return (
              <Tooltip
                slots={{ transition: Zoom }}
                title={
                  <>
                    {value.map(({ label }, index) => (
                      <p key={`${label}-${index}`} style={{ margin: 0, color: "var(--tooltip-font-color)" }}>
                        {label}
                      </p>
                    ))}
                  </>
                }
              >
                <span style={{ color: "var(--primary-font-color)" }}>
                  {`${value.length - (props.enableAllOption && value[0].value === SELECT_ALL_VALUE ? 1 : 0)} Selected`}
                </span>
              </Tooltip>
            );
          } else {
            return null;
          }
        };
      }
    }
    if (props.hideArrow) subComponents["DropdownIndicator"] = () => null;
    return subComponents;
  };

  // Handlers

  const onChange = (newValue?: MSelectOption | MSelectOption[]) => {
    // Check if all options was selected
    if (
      (props.isMulti && Array.isArray(newValue) && newValue.some((o) => o.value === SELECT_ALL_VALUE)) ||
      (!Array.isArray(newValue) && newValue?.value === SELECT_ALL_VALUE)
    ) {
      props.onChange(props.options);
      return;
    }

    // Check if all options was deselected
    if (
      props.isMulti &&
      Array.isArray(props.value) &&
      props.value.length === props.options.length &&
      ((Array.isArray(newValue) && !newValue.some((o) => o.value === SELECT_ALL_VALUE)) ||
        (!Array.isArray(newValue) && newValue?.value !== SELECT_ALL_VALUE))
    ) {
      props.onChange(null);
      return;
    }

    // Remove all option
    if (props.isMulti && Array.isArray(newValue)) {
      props.onChange(newValue.filter((o) => o.value !== SELECT_ALL_VALUE));
      return;
    }

    props.onChange(newValue);
  };

  const onFocus = () => {
    if (props.onFocus) props.onFocus();
  };

  // Render

  const getOptions = () => {
    let optionsResult = [...props.options];
    if (props.sortFunc) {
      optionsResult.sort(props.sortFunc);
    }
    if (props.enableAllOption) {
      optionsResult.unshift({ value: SELECT_ALL_VALUE, label: props.allOptionLabel });
    }
    return optionsResult;
  };
  const options = getOptions();

  const SelectComponent = getSelectComponent();
  // Disable portal by default to fix positioning issues in dialogs. Use getMenuPortalTarget to enable portal when needed.
  const portalMenuTarget = props.getMenuPortalTarget ? props.getMenuPortalTarget() : null;
  const selectComponents = props.noOptionComponentStyles ? null : getSubComponents();
  const filterOption = createFilter({ ignoreAccents: false }); // Speeds up searching and also will ignore accents on characters while searching

  return (
    <SelectComponent
      id={props.id}
      className={classes(props.className, ReactSelectClassName)}
      classNamePrefix="react-select"
      defaultValue={props.defaultValue}
      value={props.value}
      options={options}
      components={selectComponents}
      // @ts-ignore - react-select typing issue
      menuPortalTarget={portalMenuTarget}
      onChange={onChange}
      closeMenuOnSelect={!props.isMulti}
      menuPlacement={props.menuPlacement || "bottom"}
      hideSelectedOptions={false}
      filterOption={filterOption}
      onFocus={onFocus}
      // @ts-ignore - react-select typing issue
      ref={selectControlRef}
      isClearable={props.isClearable}
      isMulti={props.isMulti}
      styles={styles}
      placeholder={props.placeholder}
      label={props.label}
      itemLabel={props.itemLabel}
      itemValue={props.itemValue}
      name={props.name}
      onBlur={props.onBlur}
      isValidNewOption={props.isValidNewOption}
    />
  );
};

MSelect.defaultProps = {
  options: [],
  enableAllOption: true,
  allOptionLabel: "Select All",
  getMenuPortalTarget: null,
  creatable: false,
  noPadding: false,
  noOptionComponentStyles: false,
  isMulti: false,
  hideMultiSelected: false,
  onChange: null,
  isClearable: true,
  sortFunc: null,
};

export default MSelect;
